<script lang="ts">
export default {
  name: 'TeemillCloud',
};
</script>

<script setup lang="ts">
import { useBreakpoints } from '@/utils/breakpoints';
import { computed, onMounted, ref, watch, watchEffect } from 'vue';

import { App, AppBase, AppHeader, AppHeaderSearch, AppSidebar } from '@teemill/modules/app';

import { ModalContainer } from '@teemill/modules/modals';
import { NotificationContainer } from '@teemill/modules/notifications';

import TeemillHeaderContent from '@/components/TeemillHeaderContent.vue';
import TeemillHeaderLogo from '@/components/TeemillHeaderLogo.vue';
import LoadingScreen from '@/components/TeemillLoadingScreen.vue';
import TeemillMenu from '@/components/TeemillMenu.vue';
import TeemillNavBar from '@/components/TeemillNavBar.vue';
import TeemillTermsPopup from '@/components/TeemillTermsPopup.vue';

import * as layouts from '@/layouts';

import { cloudDark, cloudLight } from '@teemill/modules/theme-builder/themes';

import { useLoadingState } from '@/loading';
import { useBlog } from '@/services/blog';
import { usePlatform } from '@/services/platform';
import { useUser } from '@/services/user';
import { setDocumentTitle } from '@/utils/setDocumentTitle';
import { TmlScrollContainer } from '@teemill/components';
import { useMenu } from '@teemill/modules/menu';
import { useNotifications } from '@teemill/modules/notifications';
import type { Terms } from '@teemill/platform';
import { computedAsync, useFavicon } from '@vueuse/core';

import { useAuth } from '@/services/auth';
import { useFAQs } from '@/services/faqs';
import { useEvents } from '@/utils/useEvents';
import { captureException, captureMessage } from '@sentry/vue';
import { isEmbeddedDashboardHandlerActive } from '@/utils/embeddedDashboardHandler';
import { isHttpError } from '@teemill/modules/utils';
import { useRoute, useRouter } from 'vue-router';
import { useProjects } from './services/projects';
import { image } from './utils/image';
import { usePush } from './utils/usePush';

const { project: activeProject } = useProjects();

const unreadNotificationCount = computed(() => {
  return useNotifications()
    .queue('shove')
    .filter((n) => {
      const projectId = n.props.notification.group?.split(':')[1];
      return !n.props.notification.readAt && (!projectId || projectId === activeProject.value?.id);
    }).length;
});

const {
  active: platform,
  public: { getActiveTerms, signTerms },
  logSearch,
} = usePlatform();

const favicon = computed(() => {
  if (platform.value?.favicon) {
    return platform.value.favicon;
  }

  const desktopLightLogo = platform.value?.logos.find(
    (logo) => logo.meta.device === 'desktop' && logo.meta.theme === 'light',
  );

  if (desktopLightLogo) {
    return image(desktopLightLogo.url).size(48, 48).fit('contain').toString();
  }

  return undefined;
});

useFavicon(favicon);

const app = App.get('teemill');
app.setTheme(window.localStorage.getItem('theme') === 'cloudDark' ? cloudDark : cloudLight);

const events = useEvents();
events.user.subscribe('theme', (theme: string) => {
  if (theme === 'cloudDark') {
    app.setTheme(cloudDark);
    window.localStorage.setItem('theme', 'cloudDark');
  } else if (theme === 'cloudLight') {
    app.setTheme(cloudLight);
    window.localStorage.setItem('theme', 'cloudLight');
  }
});

const { loaded } = useLoadingState();

const menu = useMenu();
const inputFocused = ref(false);

const breakpoints = useBreakpoints();
const smallerThanXs = breakpoints.smaller('xs');

const loading = computed(() => !loaded.value);

const router = useRouter();
const route = useRoute();

watchEffect(() => {
  let metaTitle =
    typeof route.meta.title === 'function' ? route.meta.title(route) : route.meta.title || '';

  if (unreadNotificationCount.value) {
    metaTitle = `(${unreadNotificationCount.value}) ${metaTitle}`;
  }

  setDocumentTitle(metaTitle, {
    project: activeProject.value?.name,
    platform: platform.value?.name,
  });
});

onMounted(() => {
  document.documentElement.style.setProperty(
    '--scrollbar-width',
    window.innerWidth - document.documentElement.offsetWidth + 'px',
  );
});

const { active: user } = useUser();
const platformTerms = ref<Terms>();
const showTermsPopup = ref(false);

watch([user, platform], ([user, platform]) => {
  if (user === undefined || platform === undefined || activeProject.value === undefined) {
    return;
  }
  getActiveTerms()
    .then((terms) => {
      if (!terms.id) {
        captureMessage('No terms id', { extra: terms });
        return;
      }
      platformTerms.value = terms;
    })
    .catch(() => {});
});

watch(platformTerms, (terms) => {
  if (!terms || !user.value) {
    return;
  }
  const hasSignedCurrentTerms = user.value.signedPlatformTermsLogs.some(
    (log) => log.id === terms.id && log.version === terms.version,
  );

  if (!hasSignedCurrentTerms) {
    showTermsPopup.value = true;
  }
});

const agreeTerms = () => {
  showTermsPopup.value = false;
  if (!platform.value || !platformTerms.value) {
    return;
  }

  signTerms(platform.value.id, activeProject.value?.id, platformTerms.value.id)
    ?.then((response) => {
      user.value?.signedPlatformTermsLogs.push({ id: response.id, version: response.version });
      useNotifications().nudge('Terms and conditions accepted');
    })
    .catch(() => {
      useNotifications().nudge('Failed to accept terms', {
        level: 'danger',
      });
    });
};

const blogsList = computedAsync(() => {
  if (!activeProject.value) {
    return [];
  }
  const { blogs } = useBlog();

  const auth = useAuth();

  if (auth.isAuthenticatedAs('user')) {
    return blogs
      .list({
        search: 'visibility:published type:parent-platform,project ',
      })
      .then((res) => res.data.blogs);
  }
}, []);

const faqsList = computedAsync(() => {
  if (!activeProject.value) {
    return [];
  }
  const { faqs } = useFAQs(activeProject.value.id);
  return faqs.list({ search: 'visibility:platform' }).then((res) => res.faqs);
});
const searchQuery = ref('');
const searchResultsLength = ref(0);

watch(searchQuery, (query) => {
  if (query.length < 2 || platform.value === undefined) {
    return;
  }
  logSearch(query, searchResultsLength.value, 'platform_search').catch((e) => {
    if (!isHttpError(e) || e.response === undefined) {
      captureException(e);
    }
  });
});
// ! Remove after website migration
//   This is a temporary solution to handle headless navigation between
//   iframes of dashboard, and the new cloud app.
window.addEventListener(
  'message',
  (message) => {
    const { event, data } = message.data;

    switch (event) {
      case 'headless-navigation':
        if (isEmbeddedDashboardHandlerActive()) {
          break;
        }

        try {
          router.push(data.to);
        } catch (error) {
          captureMessage('Failed to navigate to headless route', {
            extra: {
              headlessNavigation: data,
              error: error,
            },
          });
        }
        break;
      case 'open-ai-studio': {
        if (isEmbeddedDashboardHandlerActive()) {
          break;
        }

        const allowedOrigins = Array.from(document.getElementsByTagName('iframe'))
          .filter((i) => i.src)
          .map((i) => new URL(i.src).origin);

        if (message.source && allowedOrigins.includes(message.origin)) {
          const aiStudioRoute = data.type === 'product' ? 'mockup' : data.type; // Would be better to change the dashboard calling side too

          router.push({
            name: `${route.name as string}.ai-studio.${aiStudioRoute}`,
            query: { ...route.query, 'stay-open': 'evaluating' },
          });
        }
        break;
      }
      default:
        break;
    }
  },
  false,
);

/**
 * Register the service worker for push notifications
 */
const { register } = usePush();

onMounted(register);
</script>

<template>
  <AppBase :app="app" :layouts="layouts" :loading="loading">
    <template #announcement>
      <TmlScrollContainer>
        <NotificationContainer name="announcement" :limit="1" />
      </TmlScrollContainer>
    </template>
    <template #header>
      <AppHeader>
        <template #logo>
          <TeemillHeaderLogo :hide-text="inputFocused && smallerThanXs" />
        </template>
        <template #content>
          <TeemillHeaderContent @focus="inputFocused = true" @blur="inputFocused = false" />
        </template>
      </AppHeader>
    </template>
    <template #sidebar>
      <AppSidebar>
        <AppHeaderSearch
          v-model:query="searchQuery"
          v-model:searchResultsLength="searchResultsLength"
          :faqs="faqsList"
          :blogs="blogsList"
          :project="activeProject"
          class="block sm:hidden mx-auto max-w-xs w-full px-2"
        />
        <TeemillMenu :menu-items="menu.items.value" />
      </AppSidebar>
    </template>
    <template #content>
      <RouterView v-slot="{ Component }">
        <!-- <Transition name="page"> -->
        <Component :is="Component" />
        <!-- </Transition> -->
      </RouterView>
    </template>
    <template #after-content />
    <template #footer />
    <template #subfooter />
    <template #nav-bar>
      <TeemillNavBar :items="menu.items.value" />
    </template>
    <template #global>
      <div class="fixed top-0 z-modal">
        <div id="popup-teleport-anchor"></div>
        <ModalContainer transition="popup" />
      </div>

      <div class="fixed top-3 w-full flex pointer-events-none justify-center z-notification">
        <ModalContainer transition="nudge" name="nudge" />
      </div>

      <TeemillTermsPopup
        v-if="showTermsPopup && platformTerms"
        :terms="platformTerms"
        @agree="agreeTerms"
      />
    </template>
    <template #loader>
      <loading-screen />
    </template>
  </AppBase>
</template>

<style scoped>
.page-leave-active,
.page-enter-active {
  transition: all 0.15s ease-out;
  opacity: 1;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}
</style>
